import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { TestReportMember } from '../../hooks/use-organization-tests';
import type { DateRange } from '../../helpers/get-current-test-period-range';
import { MemberTestsPanel } from './member-tests-panel';

interface MemberRowProps {
  member: TestReportMember;
  organizationId: number | undefined;
  range: DateRange;
  isExpanded: boolean;
  onToggle: () => void;
}

function formatAverageScore(averageScore: number | null): string {
  if (averageScore === null) return '—';
  return `${averageScore}%`;
}

function MemberSummary({ member }: { member: TestReportMember }) {
  return (
    <>
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <span className="text-sm font-medium text-primary">
            {member.name[0]}
            {member.surname[0]}
          </span>
        </div>
        <Link
          to={`/users/${member.id}/stats`}
          className="text-sm font-medium truncate hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {member.name} {member.surname}
        </Link>
      </div>
      <span className="text-sm text-right">{member.testsCount}</span>
      <span className="text-sm text-right font-medium">
        {formatAverageScore(member.averageScore)}
      </span>
    </>
  );
}

export function MemberRow({
  member,
  organizationId,
  range,
  isExpanded,
  onToggle,
}: MemberRowProps) {
  return (
    <div>
      <div
        className="grid grid-cols-[1fr_80px_120px_40px] gap-4 items-center px-4 py-3 hover:bg-accent/5 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <MemberSummary member={member} />
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={`Show tests for ${member.name} ${member.surname}`}
          className="flex items-center justify-center"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </div>
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-3 bg-accent/5 border-t">
            <MemberTestsPanel
              organizationId={organizationId}
              userId={member.id}
              range={range}
              isOpen={isExpanded}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
